import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import { nextDepartureService } from '../../../services/nextDepartureService';


const NextDepartureCard = ({ countdown, departureInfo: propDepartureInfo }) => {
  const [departureInfo, setDepartureInfo] = useState(propDepartureInfo);
  const [loading, setLoading] = useState(!propDepartureInfo);

  useEffect(() => {
    if (propDepartureInfo) {
      setDepartureInfo(propDepartureInfo);
      setLoading(false);
    } else {
      loadDepartureInfo();
    }
  }, [propDepartureInfo]);

  const loadDepartureInfo = async () => {
    try {
      const data = await nextDepartureService?.getActive();
      setDepartureInfo(data);
    } catch (error) {
      console.error('Error loading departure info:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="w-full bg-white rounded-xl shadow-md p-6 animate-pulse border-l-4 border-green-600">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="h-20 bg-gray-200 rounded-lg flex-1"></div>
          <div className="h-20 bg-gray-200 rounded-lg flex-1"></div>
          <div className="h-20 bg-gray-200 rounded-lg flex-1"></div>
        </div>
      </div>
    );
  };

  if (!departureInfo) {
    return null;
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'Date non disponible';
    return dateString;
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.5 }}
      className="w-full bg-white rounded-xl shadow-lg border-l-8 border-green-600 overflow-hidden"
    >
      <div className="p-6">
        {/* Header with Animated Ship Icon */}
        <div className="flex items-center gap-3 mb-4">
          <motion.div
            animate={{ 
              x: [0, 10, 0],
            }}
            transition={{ 
              duration: 3,
              repeat: Infinity,
              ease: "easeInOut"
            }}
            className="bg-green-100 p-3 rounded-lg"
          >
            <Icon name="Ship" className="w-6 h-6 text-green-600" />
          </motion.div>
          <div className="flex-1">
            {/* SEO-Optimized H2 Title */}
            <h2 className="text-2xl font-bold text-gray-900">Prochain départ maritime France – Abidjan</h2>
          </div>
        </div>

        {/* SEO-Optimized H3 Subtitle */}
        <h3 className="text-lg font-semibold text-gray-800 mb-3">
          Départ conteneur groupage Paris vers le port d&apos;Abidjan
        </h3>

        {/* SEO-Optimized Paragraph */}
        <p className="text-sm text-gray-700 leading-relaxed mb-6">
          Nos expéditions maritimes vers la Côte d&apos;Ivoire sont organisées deux fois par mois. Les colis et barriques sont regroupés en conteneur sécurisé avant embarquement vers le port d&apos;Abidjan.
        </p>

        {/* Structured Info Block */}
        <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-lg p-5 mb-6 border border-green-200">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-start gap-3">
              <span className="text-2xl">📍</span>
              <div>
                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Dépôt</p>
                <p className="text-sm font-bold text-gray-900">Région parisienne</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <span className="text-2xl">🚢</span>
              <div>
                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Destination</p>
                <p className="text-sm font-bold text-gray-900">Port d&apos;Abidjan</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <span className="text-2xl">📦</span>
              <div>
                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Type d&apos;envoi</p>
                <p className="text-sm font-bold text-gray-900">Colis, barriques, conteneurs</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <span className="text-2xl">📅</span>
              <div>
                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Fréquence</p>
                <p className="text-sm font-bold text-gray-900">2 départs par mois</p>
              </div>
            </div>
          </div>
        </div>

        {/* Countdown Timer - ALWAYS VISIBLE (removed isCountdownValid condition) */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-orange-50 border-l-4 border-orange-500 rounded-lg p-4 mb-6"
        >
          <div className="flex items-center gap-3">
            <Icon name="Clock" className="w-5 h-5 text-orange-600" />
            <div>
              <p className="text-sm font-medium text-orange-900 mb-1">
                Il reste <span className="font-bold text-lg">{countdown?.days || 0}</span> jours pour déposer vos marchandises
              </p>
              <p className="text-xs text-orange-700">
                {countdown?.hours || 0}h {countdown?.minutes || 0}min restantes
              </p>
            </div>
          </div>
        </motion.div>

        {/* Info Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Departure Date */}
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="Calendar" className="w-5 h-5 text-green-600" />
              <p className="text-xs text-gray-600 font-medium">Date de départ</p>
            </div>
            <p className="text-lg font-bold text-gray-900">{formatDate(departureInfo?.departureDate)}</p>
          </div>

          {/* Vessel Name */}
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="Ship" className="w-5 h-5 text-green-600" />
              <p className="text-xs text-gray-600 font-medium">Navire</p>
            </div>
            <p className="text-lg font-bold text-gray-900">{departureInfo?.vesselName || 'N/A'}</p>
            <p className="text-xs text-gray-600 mt-1">{departureInfo?.port || 'Anvers → Abidjan'}</p>
          </div>

          {/* Arrival Date */}
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="MapPin" className="w-5 h-5 text-green-600" />
              <p className="text-xs text-gray-600 font-medium">Arrivée Abidjan</p>
            </div>
            <p className="text-lg font-bold text-gray-900">{formatDate(departureInfo?.estimatedArrival)}</p>
          </div>
        </div>

        {/* Bottom Info */}
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Customs & Delivery */}
          <div className="bg-green-50 rounded-lg p-4 border border-green-200">
            <div className="flex items-start gap-3">
              <Icon name="Clock" className="w-5 h-5 text-green-600 mt-0.5" />
              <div>
                <p className="text-xs text-green-900 font-medium mb-2">Délais après arrivée</p>
                <div className="space-y-1 text-xs text-green-800">
                  <p>• Douane : 7-10 jours</p>
                  <p>• Livraison : 7 jours</p>
                </div>
              </div>
            </div>
          </div>

          {/* Disclaimer */}
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
            <div className="flex items-start gap-3">
              <Icon name="Info" className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-blue-900 leading-relaxed">
                Dates indicatives, variables selon conditions maritimes et douanières.
              </p>
            </div>
          </div>
        </div>

        {/* Reassurance Text at Bottom */}
        <div className="mt-6 bg-gradient-to-r from-green-50 to-green-100 rounded-lg p-4 border-l-4 border-green-600">
          <div className="flex items-start gap-3">
            <Icon name="CheckCircle" className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-green-900 font-medium leading-relaxed">
              Chaque expédition bénéficie d&apos;un suivi logistique précis jusqu&apos;à l&apos;arrivée à Abidjan.
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default NextDepartureCard;